import { FormHelperText } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import translate from '@src/services/translate'
import { forwardRef, useEffect, useId, useState } from 'react'
import { FieldError } from 'react-hook-form'
import { SelectValueListItem } from '../data/types'
import translateErrorCode from './TranslateError'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

interface Props {
  title: string
  content: SelectValueListItem[]
  error?: FieldError
  defaultValues?: string[] | number[]
  disabled?: boolean
  onValueChanged?: (value: string[] | number[]) => void
}

/** @deprecated */
const MultipleSelectCheckmarks = forwardRef<HTMLSelectElement, Props>(
  ({ title, content, error, defaultValues = [], disabled = false, onValueChanged, ...otherProps }: Props, ref) => {
    const [selectedElements, setSelectedElements] = useState<string[] | number[]>(defaultValues ?? [])

    useEffect(() => {
      setSelectedElements(defaultValues ?? [])
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content])

    const handleChange = (event: SelectChangeEvent<typeof selectedElements>) => {
      const {
        target: { value },
      } = event
      const values = typeof value === 'string' ? value.split(',') : value

      setSelectedElements(
        // On autofill we get a stringified value.
        values,
      )

      if (onValueChanged) onValueChanged(values)
    }

    const labelId = useId()

    return (
      <div>
        <FormControl fullWidth sx={{ minWidth: 120 }} disabled={disabled}>
          <InputLabel id={labelId} shrink>
            {title}
          </InputLabel>
          <Select
            inputRef={ref}
            inputProps={{ ...otherProps }}
            labelId={labelId}
            label={title}
            multiple
            value={selectedElements}
            onChange={handleChange}
            input={<OutlinedInput notched label={title} />}
            renderValue={(selected) => {
              const labels: string[] = []
              selected.forEach((item: string | number) => {
                const itemInContent = content.find((x) => x.value === item)
                if (itemInContent) labels.push(translate(itemInContent.label as string))
              })
              return labels.join(', ')
            }}
            MenuProps={MenuProps}
          >
            {content.map((element) => (
              <MenuItem key={element.value} value={element.value}>
                <Checkbox checked={selectedElements.findIndex((x: string | number) => x === element.value) > -1} />
                <ListItemText primary={translate(`${element.label}`)} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{translateErrorCode(error)}</FormHelperText>
        </FormControl>
      </div>
    )
  },
)

MultipleSelectCheckmarks.displayName = 'MultipleSelectCheckmarks'

export default MultipleSelectCheckmarks
